import currency from "currency.js";
import moment from "moment";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { nanosToSeconds } from "../../utils/date";
import { formatCurrencyWithSubscript } from "../../utils/format";
import { formatKnownValue } from "../../utils/unknownNumbers";
import { Chevron } from "../shared/Chevron/style";
import { Align } from "../shared/Table/style";
import TickerLogo from "../shared/TickerLogo";
import { IModifiedPosition } from "./model";
import {
  computeGreeksSummary,
  computePositionValuePnlSummary,
  greeksAccessor,
  positionValuePnlAccessor,
} from "./OptionPositionTableRow.helpers";
import { AssetAndLabels, ExpiryColumn } from "./style";

interface IHeaderCellDeps {
  asset: any;
  t: any;
  expanded: boolean;
  allPositions: any[];
  expandedExpiries: string[] | undefined;
  positionValuePnlSummary: ReturnType<typeof computePositionValuePnlSummary>;
  greeksSummary: ReturnType<typeof computeGreeksSummary>;
}

export const renderHeaderCellContentImpl = (
  column: ITableColumn<IModifiedPosition>,
  expiry: string | undefined,
  deps: IHeaderCellDeps
) => {
  const {
    asset,
    t,
    expanded,
    allPositions,
    expandedExpiries,
    positionValuePnlSummary,
    greeksSummary,
  } = deps;
  const { accessor, align } = column;

  if (accessor === "instrument_name") {
    if (expiry) {
      return (
        <ExpiryColumn expanded={expanded}>
          <div>
            {moment.unix(nanosToSeconds(expiry)).format("DD MMM YY")}
            <Chevron
              direction={
                (expandedExpiries || []).includes(expiry) ? "up" : "down"
              }
            />
          </div>
        </ExpiryColumn>
      );
    }
    return (
      <AssetAndLabels>
        <TickerLogo asset={asset} size={32} />
        <div>
          <span>
            {asset} {t("options")}
            <Chevron direction={expanded ? "up" : "down"} />
          </span>
          <span>
            {allPositions.length} {t("positions")}
          </span>
        </div>
      </AssetAndLabels>
    );
  }

  if (positionValuePnlAccessor.includes(accessor as any) && !expiry) {
    let value = "";
    const summary = expiry
      ? positionValuePnlSummary.optionByExpirires[expiry]
      : positionValuePnlSummary.total;
    if ((accessor as any) === "positionValue") {
      value = formatKnownValue(summary.positionValue, summary.pricePrecision);
    }
    if ((accessor as any) === "maintenance_margin") {
      value = formatKnownValue(summary.maintenance_margin);
    }
    if ((accessor as any) === "unrealized_pnl") {
      value = formatCurrencyWithSubscript(summary.pnl, {
        showPositiveSign: true,
      });
    }
    return <Align align={align}>{value}</Align>;
  }

  if (greeksAccessor.includes(accessor as any) && !expiry) {
    let value = "";
    const summary = expiry
      ? greeksSummary.optionByExpiries[expiry]
      : greeksSummary.totalGreeks;
    if ((accessor as any) === "option.delta") {
      value = summary.delta.toFixed(2);
    }
    if ((accessor as any) === "option.gamma") {
      value = summary.gamma.toFixed(2);
    }
    if ((accessor as any) === "option.vega") {
      value = summary.vega.toFixed(2);
    }
    if ((accessor as any) === "option.theta") {
      value = currency(summary.theta).format();
    }
    return <Align align={align}>{value}</Align>;
  }

  return null;
};
